/**
 * PDA 扫码提示音（仓储备货/收发货）
 * 成功：经典「叮咚」上升双音（清脆好辨）
 * 异常：高频方波连鸣（尖锐醒目）
 */

let audioContext = null;

function getCtor() {
  return window.AudioContext || window.webkitAudioContext || null;
}

export function initScanAudio() {
  if (audioContext) return audioContext;
  try {
    const Ctor = getCtor();
    if (!Ctor) return null;
    audioContext = new Ctor();
    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => {});
    }
  } catch (e) {
    console.warn("创建扫码音频上下文失败", e);
  }
  return audioContext;
}

/** 用户手势解锁（浏览器自动播放策略） */
export function unlockScanAudio() {
  const ctx = initScanAudio();
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  return ctx;
}

function ensureReady() {
  const ctx = initScanAudio();
  if (!ctx) return null;
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  return ctx;
}

function disconnectLater(...nodes) {
  return () => {
    for (const n of nodes) {
      try {
        n.disconnect();
      } catch (e) {
        /* ignore */
      }
    }
  };
}

/** 异常用：尖锐方波 */
function playSharpTone(ctx, { freq, start, duration, peak = 1 }) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(freq, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.003);
  gain.gain.setValueAtTime(peak, start + Math.max(duration - 0.02, 0.02));
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
  osc.onended = disconnectLater(osc, gain);
}

/**
 * 成功单音：三角波主音 + 轻微方波加锐度（PDA 小喇叭更清楚）
 * 类似收银/扫码枪「叮」
 */
function playChimeTone(ctx, { freq, start, duration, peak = 0.9 }) {
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, start);
  master.gain.exponentialRampToValueAtTime(peak, start + 0.006);
  master.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  master.connect(ctx.destination);

  const tri = ctx.createOscillator();
  tri.type = "triangle";
  tri.frequency.setValueAtTime(freq, start);
  const triGain = ctx.createGain();
  triGain.gain.value = 0.85;
  tri.connect(triGain);
  triGain.connect(master);

  // 叠一层很轻的方波，让小喇叭更「亮」
  const sq = ctx.createOscillator();
  sq.type = "square";
  sq.frequency.setValueAtTime(freq, start);
  const sqGain = ctx.createGain();
  sqGain.gain.value = 0.22;
  sq.connect(sqGain);
  sqGain.connect(master);

  tri.start(start);
  sq.start(start);
  tri.stop(start + duration + 0.02);
  sq.stop(start + duration + 0.02);
  tri.onended = disconnectLater(tri, sq, triGain, sqGain, master);
}

/**
 * 成功：经典上升双音「叮—咚」（约 2.0k → 2.8k）
 * 短促、好听、和异常蜂鸣差很大，仓库里也好认
 */
export function playScanSuccessBeep() {
  try {
    const ctx = ensureReady();
    if (!ctx) return;
    const t = ctx.currentTime;
    playChimeTone(ctx, { freq: 2000, start: t, duration: 0.1, peak: 0.95 });
    playChimeTone(ctx, {
      freq: 2800,
      start: t + 0.11,
      duration: 0.14,
      peak: 1,
    });
  } catch (e) {
    console.warn("播放扫码成功提示音失败", e);
  }
}

/**
 * 异常：五声刺耳蜂鸣，约 2.8kHz
 */
export function playScanErrorBeep() {
  try {
    const ctx = ensureReady();
    if (!ctx) return;
    const t = ctx.currentTime;
    const pulse = 0.08;
    const gap = 0.05;
    for (let i = 0; i < 5; i++) {
      playSharpTone(ctx, {
        freq: 2800,
        start: t + i * (pulse + gap),
        duration: pulse,
        peak: 1,
      });
    }
  } catch (e) {
    console.warn("播放扫码异常提示音失败", e);
  }
}

export function closeScanAudio() {
  if (!audioContext) return;
  audioContext.close().catch(() => {});
  audioContext = null;
}
